import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import PublicFaqSection from "@/components/PublicFaqSection";
import { logger } from '@/lib/logger';
import { trackEvent, FUNNEL_EVENTS } from '@/lib/analytics';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PriceDetail {
  price: string;
  priceFrequency: string;
  priceIdKey: string;
}

interface Plan {
  id: string;
  name: string;
  jotformPlan: string;
  description: string;
  features: string[];
  monthly: PriceDetail;
  annual?: PriceDetail;
  cta: string;
  highlighted?: boolean;
}

const plansData: Plan[] = [
  {
    id: "starter",
    name: "Starter",
    jotformPlan: "Jotform Free",
    description: "Free 14-day trial, then a one-time $20 lifetime fee.",
    features: [
      "14-day free trial — no credit card required",
      "Up to 5 forms",
      "Daily automated backups",
      "14-day backup retention",
      "Manual export",
      "Enterprise-grade encryption",
      "One-time payment — no recurring fees",
    ],
    monthly: { price: "$20", priceFrequency: " one-time", priceIdKey: "starter" },
    cta: "Start 14-Day Free Trial",
  },
  {
    id: "bronze",
    name: "Bronze",
    jotformPlan: "Jotform Bronze",
    description: "For growing teams with more forms to protect.",
    features: [
      "14-day free trial — no credit card required",
      "Up to 25 forms",
      "Daily automated backups",
      "30-day backup retention",
      "Manual export",
      "Enterprise-grade encryption",
    ],
    monthly: { price: "$8", priceFrequency: "/mo", priceIdKey: "bronze_monthly" },
    annual: { price: "$80", priceFrequency: "/yr", priceIdKey: "bronze_annual" },
    cta: "Upgrade to Bronze",
  },
  {
    id: "silver",
    name: "Silver",
    jotformPlan: "Jotform Silver",
    description: "More forms, longer retention, greater peace of mind.",
    features: [
      "14-day free trial — no credit card required",
      "Up to 50 forms",
      "Daily automated backups",
      "60-day backup retention",
      "Manual export",
      "Enterprise-grade encryption",
    ],
    monthly: { price: "$12", priceFrequency: "/mo", priceIdKey: "silver_monthly" },
    annual: { price: "$120", priceFrequency: "/yr", priceIdKey: "silver_annual" },
    cta: "Upgrade to Silver",
    highlighted: true,
  },
  {
    id: "gold",
    name: "Gold",
    jotformPlan: "Jotform Gold",
    description: "For power users and organizations with critical forms.",
    features: [
      "14-day free trial — no credit card required",
      "Up to 100 forms",
      "Daily automated backups",
      "90-day backup retention",
      "Manual export",
      "Enterprise-grade encryption",
      "Priority support",
    ],
    monthly: { price: "$25", priceFrequency: "/mo", priceIdKey: "gold_monthly" },
    annual: { price: "$250", priceFrequency: "/yr", priceIdKey: "gold_annual" },
    cta: "Upgrade to Gold",
  },
];

const PricingPage = () => {
  const { user, subscription, isSubscriptionLoading, fetchSubscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const [isLoadingCheckout, setIsLoadingCheckout] = useState<string | null>(null);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");

  const [showProrationDialog, setShowProrationDialog] = useState(false);
  const [prorationDetails, setProrationDetails] = useState<any>(null);
  const [targetPriceIdKeyForUpdate, setTargetPriceIdKeyForUpdate] = useState<string | null>(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get("checkout_success") === "true") {
        trackEvent(FUNNEL_EVENTS.CHECKOUT_SUCCESS);
        toast({
            title: "Payment Successful!",
            description: "Refreshing your subscription status...",
        });
        fetchSubscriptionStatus().then(() => {
            navigate("/pricing", { replace: true });
        });
    } else if (queryParams.get("checkout_canceled") === "true") {
        toast({
            title: "Checkout Canceled",
            description: "Your subscription was not completed.",
            variant: "default"
        });
        navigate("/pricing", { replace: true });
    }

  }, [subscription, fetchSubscriptionStatus, navigate]);

  const handleSubscribe = async (planIdKey: string | null) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (!planIdKey || planIdKey === "starter_free") return;

    trackEvent(FUNNEL_EVENTS.CHECKOUT_START, { plan: planIdKey });
    setIsLoadingCheckout(planIdKey);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceIdKey: planIdKey },
      });

      if (error) throw error;

      if (data.url) {
        window.open(data.url, '_blank');
        toast({
          title: "Redirecting to Stripe...",
          description: "Please complete your subscription in the new tab. We'll refresh your status once done.",
        });

        const checkInterval = setInterval(async () => {
          await fetchSubscriptionStatus();
          if (subscription?.subscribed) {
            clearInterval(checkInterval);
            const currentActiveTier = subscription?.subscription_tier || "Unknown";
            toast({ title: "Subscription active!", description: `Your ${currentActiveTier} plan is now active.` });
          }
        }, 5000);

        setTimeout(() => {
          clearInterval(checkInterval);
        }, 120000);

      } else {
        throw new Error("Could not retrieve Stripe session URL.");
      }
    } catch (error: unknown) {
      logger.error("Subscription Error:", error);
      toast({
        variant: "destructive",
        title: "Subscription Failed",
        description: error instanceof Error ? error.message : "Could not initiate the subscription process.",
      });
    } finally {
      setIsLoadingCheckout(null);
    }
  };

  const handleSwitchBillingCycle = async (newPriceIdKey: string) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setIsLoadingUpdate(newPriceIdKey);
    setTargetPriceIdKeyForUpdate(newPriceIdKey);

    try {
      const { data: previewData, error: previewError } = await supabase.functions.invoke("update-subscription-plan", {
        body: { newPriceIdKey, confirmUpdate: false },
      });

      if (previewError) throw previewError;

      setProrationDetails(previewData);
      setShowProrationDialog(true);

    } catch (error: unknown) {
      logger.error("Proration Preview Error:", error);
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: error instanceof Error ? error.message : "Could not fetch proration details.",
      });
    } finally {
      setIsLoadingUpdate(null);
    }
  };

  const confirmBillingCycleSwitch = async () => {
    if (!targetPriceIdKeyForUpdate) return;

    setIsLoadingUpdate(targetPriceIdKeyForUpdate);
    setShowProrationDialog(false);

    try {
      const { error: updateError } = await supabase.functions.invoke("update-subscription-plan", {
        body: { newPriceIdKey: targetPriceIdKeyForUpdate, confirmUpdate: true },
      });

      if (updateError) throw updateError;

      toast({
        title: "Subscription Updated!",
        description: "Your billing cycle has been changed.",
      });
      await fetchSubscriptionStatus();

    } catch (error: unknown) {
      logger.error("Subscription Update Error:", error);
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: error instanceof Error ? error.message : "Could not update your subscription.",
      });
    } finally {
      setIsLoadingUpdate(null);
      setTargetPriceIdKeyForUpdate(null);
    }
  };

  const currentPlanName = user ? (subscription?.subscription_tier || "Starter") : null;
  const currentPriceIdKey = user ? (subscription?.active_price_id_key || "starter_free") : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <div className="flex-grow container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Simple, Transparent Pricing
          </h1>
          <p className="mt-3 text-xl text-gray-600 dark:text-gray-300 sm:mt-4">
            Plans aligned to your Jotform account. Your tier is auto-matched at signup.
          </p>
        </header>

        {user && (
          <div className="mx-auto mb-10 max-w-2xl">
            <Alert className="border-blue-200 bg-blue-50 text-blue-950 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Use your matched plan page instead</AlertTitle>
              <AlertDescription>
                Logged-in billing changes now run through the matched Jotform plan flow, not this generic pricing page.
                <div className="mt-3">
                  <Button size="sm" onClick={() => navigate("/select-tier")}>
                    Review Matched Plan
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {!user && (
          <>
        <div className="flex justify-center items-center space-x-2 mb-10">
          <Label htmlFor="billing-cycle-toggle" className={billingCycle === 'monthly' ? 'font-semibold text-primary' : 'text-gray-600 dark:text-gray-400'}>Monthly</Label>
          <Switch
            id="billing-cycle-toggle"
            checked={billingCycle === "annual"}
            onCheckedChange={(checked) => setBillingCycle(checked ? "annual" : "monthly")}
            disabled={isSubscriptionLoading || !!isLoadingCheckout || !!isLoadingUpdate}
          />
          <Label htmlFor="billing-cycle-toggle" className={billingCycle === 'annual' ? 'font-semibold text-primary' : 'text-gray-600 dark:text-gray-400'}>
            Annual
          </Label>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {plansData.map((plan) => {
            const planDetailsToDisplay = billingCycle === "annual" && plan.annual ? plan.annual : plan.monthly;
            const isCurrentDisplayedPlanExactly = user && currentPriceIdKey === planDetailsToDisplay.priceIdKey;
            const isSameTierDifferentCycle =
                user &&
                subscription?.subscribed &&
                plan.name === currentPlanName &&
                currentPriceIdKey !== planDetailsToDisplay.priceIdKey &&
                plan.id !== 'starter';

            let buttonAction: () => void = () => handleSubscribe(planDetailsToDisplay.priceIdKey);
            let buttonText = plan.cta;
            let buttonDisabled = isLoadingCheckout === planDetailsToDisplay.priceIdKey || isSubscriptionLoading || isLoadingUpdate === planDetailsToDisplay.priceIdKey;

            if (plan.id === 'starter') {
              if (user && currentPlanName === 'Starter') {
                buttonText = "Current Plan";
                buttonDisabled = true;
                buttonAction = () => {};
              } else if (!user) {
                buttonText = "Start 14-Day Free Trial";
                buttonDisabled = false;
                buttonAction = () => navigate('/auth?mode=signup');
              }
            } else if (isCurrentDisplayedPlanExactly) {
              buttonText = "Current Plan";
              buttonDisabled = true;
            } else if (isSameTierDifferentCycle) {
              buttonText = billingCycle === "annual" ? "Switch to Annual" : "Switch to Monthly";
              buttonAction = () => handleSwitchBillingCycle(planDetailsToDisplay.priceIdKey);
              buttonDisabled = isSubscriptionLoading || isLoadingUpdate === planDetailsToDisplay.priceIdKey;
            }

            return (
              <Card
                key={`${plan.id}-${billingCycle}`}
                className={`flex flex-col ${
                  isCurrentDisplayedPlanExactly
                    ? 'border-2 border-primary ring-2 ring-primary shadow-lg'
                    : plan.highlighted
                      ? 'border-2 border-primary/50 shadow-lg'
                      : 'border-gray-200'
                }`}
              >
                <CardHeader className="p-6">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl font-semibold text-foreground">{plan.name}</CardTitle>
                    {plan.highlighted && !isCurrentDisplayedPlanExactly && (
                      <span className="text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1 rounded-full">Popular</span>
                    )}
                  </div>
                  {isCurrentDisplayedPlanExactly && (
                    <p className="text-sm font-medium text-primary">Current Plan</p>
                  )}
                  <p className="text-xs text-muted-foreground">Matches {plan.jotformPlan}</p>
                  <CardDescription className="mt-1 text-gray-600 dark:text-gray-400 h-10">{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold tracking-tight text-foreground">{planDetailsToDisplay.price}</span>
                    <span className="text-base font-medium text-gray-500 dark:text-gray-400">{planDetailsToDisplay.priceFrequency}</span>
                  </div>
                </CardHeader>
                <CardContent className="flex-grow p-6">
                  <ul role="list" className="space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start">
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-500 mr-2 mt-0.5" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="p-6 bg-muted/50">
                    <Button
                      className="w-full"
                      onClick={buttonAction}
                      disabled={buttonDisabled}
                    >
                      { (isLoadingCheckout === planDetailsToDisplay.priceIdKey || isLoadingUpdate === planDetailsToDisplay.priceIdKey) ? "Processing..." : buttonText}
                    </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Enterprise CTA */}
        <div className="mt-12 text-center bg-muted/30 rounded-lg p-8 border border-dashed">
          <h3 className="text-2xl font-bold text-foreground mb-2">Enterprise</h3>
          <p className="text-xs text-muted-foreground mb-1">Matches Jotform Enterprise</p>
          <p className="text-gray-600 dark:text-gray-300 mb-4 max-w-xl mx-auto">
            Up to 200 forms, 180-day retention, and dedicated support.
          </p>
          <Button variant="outline" onClick={() => window.location.href = 'mailto:support@fab.day?subject=Enterprise%20Plan%20Inquiry'}>
            Contact Us
          </Button>
        </div>

        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-10">
          All prices are in USD. Your plan is automatically matched to your Jotform account at signup.
          <br />
          You can upgrade, downgrade, or cancel at any time.
        </p>
        <div className="-mx-4 mt-12">
          <PublicFaqSection
            title="Pricing and backup questions"
            description="What to know before choosing a FAB plan for your Jotform account."
          />
        </div>
          </>
        )}
      </div>

      {prorationDetails && (
        <AlertDialog open={showProrationDialog} onOpenChange={setShowProrationDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Subscription Change</AlertDialogTitle>
              <AlertDialogDescription>
                You are about to switch your billing cycle.
                <br />
                {prorationDetails.proration_amount_due > 0 ?
                  `You will be charged approximately ${new Intl.NumberFormat('en-US', { style: 'currency', currency: prorationDetails.proration_currency || 'USD' }).format(prorationDetails.proration_amount_due / 100)} immediately.` :
                  `A credit of approximately ${new Intl.NumberFormat('en-US', { style: 'currency', currency: prorationDetails.proration_currency || 'USD' }).format(Math.abs(prorationDetails.proration_amount_due) / 100)} will be applied to your account.`
                }
                {prorationDetails.lines?.map((line: { description: string; amount: number; period: { start: number; end: number } }, index: number) => (
                    <div key={index} className="text-xs text-gray-500 mt-1">
                        {line.description}: {new Intl.NumberFormat('en-US', { style: 'currency', currency: prorationDetails.proration_currency || 'USD' }).format(line.amount / 100)}
                        (for period {new Date(line.period.start * 1000).toLocaleDateString()} - {new Date(line.period.end * 1000).toLocaleDateString()})
                    </div>
                ))}
                <br />
                Do you want to proceed?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setTargetPriceIdKeyForUpdate(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmBillingCycleSwitch} disabled={isLoadingUpdate === targetPriceIdKeyForUpdate}>
                {isLoadingUpdate === targetPriceIdKeyForUpdate ? "Processing..." : "Confirm & Pay"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      <Footer />
    </div>
  );
};

export default PricingPage;
